import IconButton from "../../ui/IconButton/IconButton";
import Icons, {IconType} from "../../ui/IconButton/Icons";
import { AddressParser } from '@sroussey/parse-address';
import {ReactElement, ReactNode} from 'react';
import util from '../../lib/util';
import ResourceHelper from '../Resource/ResourceHelper';
import store from '../../store/store';
import upFirstLetter from "../../lib/picos/upFirstLetter";
import Address from "../../entities/address";
import Contact from "../../entities/contact";

const addressParser = new AddressParser();

export type LawPayAddress = {
    street: string,
    unit: string,
    city: string,
    state: string,
    zip: string,
}

export type StandardizedAddress = {
    street: string,
    unit: string,
    city: string,
    state: string,
    zip: string,
    county?: string,
    country?: string,
    formatted?: string,
    placeId?: string
}

/**
 * The Address class holds multiple types of information, including phone numbers, email, and web site addresses.
 *
 * The *label* of an Address is a descriptive label, like home or work.
 *
 * Note that there can be multiple labels, including duplicate labels, for the same type of address.
 *
 * For example, for a Contact we could have the following:
 *
 * AddressId     Label          Value
 * xxx-xxx-xxx   home           21309 Vista Estates Drive, Spicewood, TX 78669
 * xxx-xxx-xxx   work           20808-B Hwy 71 W, Spicewood, TX 78669
 *
 * Note that Labels can have custom names, although we will always offer as many useful choices as possible.
 * In the user interface, we will probably offer icons in addition to option names when entering.
 *
 * This helper class is meant to help the UI find and display this information quickly and easily
 *
 * Note on parse styles...
 *
 * Address Parser uses the following structure:
 *
 * {
 *   number: '1005',
 *   prefix: 'N',
 *   street: 'Gravenstein',
 *   type: 'Hwy',
 *   city: 'Sebastopol',
 *   state: 'CA',
 *   zip: '95472'
 * }
 *
 * Google uses:
 *
 * {
 *     address_components: [
 *         {
 *             "long_name": 1600,
 *             "short_name": 1600,
 *             "types": [ "street_number" ]
 *         },
 *         ... types are street_number, route, locality, administrative_area_level_2 (County),
 *             administrative_area_level_1 (State), country, postal_code. County names end in "County"
 *     ],
 *     formatted_address: "1600 Amphitheatre Parkway, Mountain View, CA 94043, USA",
 *     place_id: "...",
 *     types: [ "street_address" ]
 * }
 *
 * Our address style is:
 *
 * {
 *   street: 1415 Longshanks Dr.',
 *   unit: null,
 *   city: 'Spicewood',
 *   state: 'TX'
 *   zip: '78669',
 *   county: 'Travis',  <-- not available in the other programs!
 *   country: 'US'
 * }
 */

export type LabelOption = {
    label: string,
    value: string,
    icon: IconType
}

class AddressHelper {
    static getAddressLabelIcon = (address: Address): IconType => {
        // Show the correct Icon next to the address
        switch (address.label) {
            case 'personal':
                return Icons.HOME;
            case 'mobile':
                return Icons.MOBILE;
            case 'home':
                return Icons.HOME;
            case 'business':
                return Icons.BUSINESS;
            case 'other':
                return Icons.OTHER;
            default:
                return Icons.OTHER;
        }
    };

    /**
     * List of basic address labels
     */
    static getLabelOptions = (): LabelOption[] => {
        return [
            {label: 'home', value: 'home', icon: Icons.HOME},
            {label: 'business', value: 'business', icon: Icons.BUSINESS},
            {label: 'other', value: 'other', icon: Icons.OTHER}
        ];
    };

    /**
     * Get an address for postal/invoice
     * @param {Address} address
     * @param {boolean} [useHtml]
     * @return {ReactElement|string}
     */
    static convertAddressToParagraph = (address: Address, useHtml: boolean = false): ReactElement | string => {
        const unit = !!address.unit
                     ? /^[0-9]*$/.test(address.unit)
                       ? '#' + address.unit
                       : address.unit
                     : null;

        const hasCity = !!address.city;
        const hasState = !!address.state;

        const cityState = hasCity && hasState ? `${address.city}, ${address.state}` :
                          hasCity ? address.city : address.state;

        const cityStateZip = `${cityState} ${address.zip}`.trim();

        if (useHtml) {
            const st = address.street ? unit || cityStateZip ? <>{address.street}<br/></> : address.street : null;
            const u = unit ? cityStateZip ? <>{unit}<br/></> : unit : null;
            return <div className='u-column'>
                {st}
                {u}
                {cityStateZip}
            </div>
        }
        else return [
            address.street,
            unit,
            cityStateZip
        ]
            .filter(util.existsFilter)
            .join('\r\n');
    }

    /**
     * Convert an Outlaw address to a one-line string.
     *
     * @param {any} address - The address to output (could be an Address, PayrixAddress,  LawPayAddress)
     * @param {boolean} includeCounty
     * @param {boolean} [textOnly]
     * @returns {string} - a one-line address
     */
    static convertAddressToOneLine = (address: any, includeCounty?: boolean, textOnly?: boolean): ReactNode => {
        let l = "";

        const street = address.street || address.line1 || address.address1;

        // Output street and optional unit (shouldn't have a unit without a street)
        if (!!street) {
            l += street;

            const unit = address.unit || address.line2 || address.address2;

            if (!!unit) {
                // Is it JUST a number?  If so, put a # in front of it
                if (!!unit)
                    /^[0-9]*$/.test(unit) ? l += ', #' + unit : l += ', ' + unit;
            }
        }

        if (!!address.city) {
            if (l.length > 0) l += ', ';
            l += address.city;
        }

        const state = address.state || address.state_or_province;

        if (!!state) {
            if (l.length > 0) l += ', ';
            l += state;
        }

        const zip = address.zip || address.postal_code;

        if (!!zip) {
            if (l.length > 0) l += ' ';
            l += zip;
        }

        const country = address.country || address.country_code;
        if (!!country) {
            if (l.length > 0) l += ', ';
            l += country;
        }

        let county: string | ReactElement;
        const countyLabel = (address.country === 'CA' || address.country === 'Canada') ? ' Jurisdiction' : ' County';
        if (textOnly) county = address.county ? address.county + countyLabel : '';
        else county = address.county ? <span className="badge badge-success">{address.county}{countyLabel}</span> : '';

        if (includeCounty && !textOnly) return <div>{l.trim()} {county}</div>;
        else if (includeCounty && textOnly) return `${l.trim()}${county ? ',' : ''} ${county}`;
        else return l.trim();
    };

    /**
     * Take a parsed address and convert it to Outlaw format.
     *
     * Note that additional attributes, like placeId and county could have been added to the fragment
     * @param {Partial<PayrixAddress | LawPayAddress | StandardizedAddress>} fragment - an address in one of several acceptible formats
     * @returns {StandardizedAddress} - an Outlaw address object
     */
    static convertParserToOutlaw = (fragment: any): StandardizedAddress | null => {
        if (!fragment) return null;

        let street = '';
        if (fragment.number) street += fragment.number;
        if (fragment.prefix) street += ' ' + fragment.prefix;
        if (fragment.street) street += ' ' + fragment.street;
        if (fragment.type) street += ' ' + fragment.type;
        if (fragment.suffix) street += ' ' + fragment.suffix;
        street = street.trim();

        let unit = '';
        if (fragment.unit) unit = fragment.unit;
        else {
            if (fragment.sec_unit_type) unit = fragment.sec_unit_type;
            if (fragment.sec_unit_num) unit += ' ' + fragment.sec_unit_num;
        }
        unit = unit.trim();

        return {
            street: street ?? undefined,
            unit: unit ?? undefined,
            city: fragment.city ?? undefined,
            state: fragment.state?.toUpperCase() ?? undefined,
            zip: fragment.zip ?? undefined,
            county: fragment.county || undefined,
            country: fragment.country || 'US',
            placeId: fragment.placeId || undefined
        };
    };

    /**
     * Parse the addressString and put the pieces into the address object
     * @param {string} addressString - the address string to parse
     * @returns {StandardizedAddress} or null if it can't be parsed
     */
    static parseAddress = (addressString: string): StandardizedAddress  => {
        if (!addressString) return null;

        const regex = /\s+(US|USA)$/i;
        const stripped = addressString.replace(regex, '');
        return addressParser.parseLocation(stripped);
    };

    /**
     * Parse a Google geocode
     *
     * @param {any} geocode - geocode from Google lookup
     */
    static parseGeocode = (geocode: any) => {
        if (geocode && geocode.address_components) {
            const ac = geocode.address_components.map((c: any) => {
                return {field: c.types[0], value: c.short_name};
            });

            const gval = (field: string) => {
                const f = ac.find((c: any) => c.field === field);
                if (f) return f.value;
                else return undefined;
            };

            const street = (street_number: string, route: string) => {
                let street = undefined;
                if (street_number) street = street_number;
                if (route) {
                    if (street) street += ' ' + route;
                    else street = route;
                }

                return street;
            };

            let subpremise = gval('subpremise');
            if (subpremise && /^[0-9]/.test(subpremise)) subpremise = `#${subpremise}`;

            let county = gval('administrative_area_level_2');
            if (county) county = county.replace(/county/i, '').trimEnd();

            return {
                street: street(gval('street_number'), gval('route')),
                unit: subpremise,
                city: gval('locality'),
                county: county,
                state: gval('administrative_area_level_1')?.toUpperCase(),
                zip: gval('postal_code'),
                country: gval('country')
            };
        }
        else return null;
    };

    /**
     * Take an Address and a fragment and copy it over
     * @param {Address} address - The address to update
     * @param {object} fragment - fragment of an address (from parseAddress).  Not Outlaw format!
     * @returns {Address}
     */
    static updateAddress = (address: Address, fragment: any): Address => {
        if (!address) return null;

        // Map the parsed format to Outlaw's format
        const oparsed = AddressHelper.convertParserToOutlaw(fragment);

        // Copy the new bits over to the target address object
        // const result = Object.assign(address, oparsed);
        const result = Object.assign(address, oparsed);
        if (address.county) result.county = address.county;

        return result;
    };

    /**
     * Get a formatted delivery address for the given clients.
     *
     * @param {Contact[]} clients
     * @return {Address}
     */
    static getDeliveryAddress(clients: Contact[]): Address {
        let addresses = clients
            .map(c => {
                return store.getState()
                    .addresses.filter(a => a.entityId === c.id && a.canContact && a.status !== 'D');
            })
            .flat()

        if (addresses.length === 0) addresses = clients
            .map(c => {
                return store.getState()
                    .addresses.filter(a => a.entityId === c.id && a.status !== 'D');
            })
            .flat()

        let address: Address;

        // If there is more than one address, get the first one marked isPrimary or isCurrent
        if (addresses.filter(a => a.isPrimary).length > 0) address = addresses.filter(a => a.isPrimary)[0];
        else if (addresses.filter(a => a.isCurrent).length > 0) address = addresses.filter(a => a.isCurrent)[0];
        else address = addresses[0];

        return address;
    }

    static renderAddress(a: Address) {
        const address = AddressHelper.convertAddressToOneLine(a, true);
        const addressIcon = a.isCurrent ? Icons.ADDRESS_CURRENT : Icons.ADDRESS;
        const addressIconColor = a.isCurrent ? 'forestgreen' : 'maroon';
        const addressTitle = a.isCurrent ? 'Current Address' : 'Not Current Address';
        const dnc = a.canContact ? '' : 'DNC';

        return (
            <div key={`${a.id}`}
                 className="ResourceLine"
            >
                <div className="ResourceButton">
                    <IconButton type={ResourceHelper.getResourceLabelIcon(a)}
                                title={upFirstLetter(a.label)}
                    />
                </div>
                <div className={`ResourceText ${dnc}`}>{address}</div>
                {a.isPrimary &&
					<div className="ResourceButton">
						<IconButton type={Icons.STAR} title='Primary'/>
					</div>}
                <div className="ResourceButton">
                    <IconButton type={addressIcon} color={addressIconColor} title={addressTitle}/>
                </div>
            </div>
        );
    }
}

export default AddressHelper;
